import { Directive, ElementRef, HostListener, Input, OnInit, OnDestroy } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';

@Directive({
  selector: '[appSwipeToClose]',
})
export class SwipeToCloseDirective implements OnInit, OnDestroy {
  @Input() appSwipeToClose: MatBottomSheetRef<any>;
  @Input() swipeThreshold: number = 100; // Umbral en píxeles para cerrar
  @Input() swipeZoneHeight: number = 80; // Altura de la zona donde se activa el swipe (en píxeles)
  @Input() forceSwipeInZone: boolean = true; // Forzar swipe en la zona superior sin validar scroll

  private startY: number = 0;
  private currentY: number = 0;
  private isDragging: boolean = false;
  private element: HTMLElement;
  private initialTouchY: number = 0;
  private swipeZoneDetected: boolean = false;
  private touchStartTime: number = 0; // Para detectar taps vs swipes

  constructor(private el: ElementRef) {
    this.element = this.el.nativeElement;
  }

  ngOnInit(): void {
    // Configuración mejorada para compatibilidad con Android
    this.element.style.touchAction = 'pan-y'; // Permitir solo scroll vertical
  }

  ngOnDestroy(): void {
    // Limpiar estilos al destruir
    if (this.element) {
      this.element.style.touchAction = '';
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    // Resetear estado previo
    this.isDragging = false;
    this.swipeZoneDetected = false;
    this.touchStartTime = Date.now();

    // Validar que tenemos al menos un touch
    if (!event.touches || event.touches.length === 0) {
      return;
    }

    // Guardar la posición Y inicial del touch
    this.initialTouchY = event.touches[0].clientY;
    this.startY = event.touches[0].clientY;

    // Obtener la posición del elemento de manera más robusta
    const rect = this.element.getBoundingClientRect();
    const touchY = event.touches[0].clientY;
    const relativeY = touchY - rect.top;

    // Debug para Android
    console.log('TouchStart - Android Debug:', {
      touchY,
      rectTop: rect.top,
      relativeY,
      swipeZoneHeight: this.swipeZoneHeight,
    });

    // Solo activar el swipe si el toque está en la zona superior (primeros N píxeles)
    if (relativeY > this.swipeZoneHeight) {
      console.log('TouchStart - Fuera de zona de swipe');
      return;
    }

    this.swipeZoneDetected = true;
    console.log('TouchStart - En zona de swipe, iniciando dragging');

    // Si forceSwipeInZone está habilitado, permitir siempre el gesto en la zona superior
    if (this.forceSwipeInZone) {
      this.isDragging = true;

      // Prevenir el comportamiento de pull-to-refresh del navegador
      // En Android es crucial hacer esto inmediatamente
      if (event.cancelable) {
        event.preventDefault();
      }
      event.stopPropagation();

      // Encontrar el contenedor del bottom sheet y ajustar su posición
      const bottomSheetContainer = this.findBottomSheetContainer();
      if (bottomSheetContainer) {
        bottomSheetContainer.style.transition = 'none';
        console.log('TouchStart - Container encontrado y preparado');
      }
      return;
    }

    // Lógica original para otros casos (simplificada para Android)
    this.isDragging = true;

    // Prevenir comportamientos del navegador
    if (event.cancelable) {
      event.preventDefault();
    }
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging || !this.swipeZoneDetected) {
      return;
    }

    // Prevenir scroll y refresh
    if (event.cancelable) {
      event.preventDefault();
    }
    event.stopPropagation();

    this.currentY = event.touches[0].clientY;
    const deltaY = this.currentY - this.startY;

    // Solo permitir arrastre hacia abajo
    if (deltaY > 0) {
      const bottomSheetContainer = this.findBottomSheetContainer();
      if (bottomSheetContainer) {
        // Aplicar transformación directa para mejor rendimiento
        bottomSheetContainer.style.transform = `translateY(${deltaY}px)`;
      }
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.isDragging || !this.swipeZoneDetected) {
      return;
    }

    this.isDragging = false;
    this.swipeZoneDetected = false;
    const deltaY = this.currentY - this.startY;
    const touchDuration = Date.now() - this.touchStartTime;

    // Si fue un tap muy rápido (menos de 200ms) y poco movimiento, no hacer nada
    if (touchDuration < 200 && Math.abs(deltaY) < 10) {
      return;
    }

    const bottomSheetContainer = this.findBottomSheetContainer();

    if (bottomSheetContainer) {
      // Restaurar transición
      bottomSheetContainer.style.transition = 'transform 0.3s ease-out';

      if (deltaY > this.swipeThreshold) {
        // Cerrar si se superó el umbral
        console.log('TouchEnd - Cerrando bottom sheet');
        // Animar salida
        bottomSheetContainer.style.transform = 'translateY(100%)';
        setTimeout(() => {
          this.appSwipeToClose.dismiss();
        }, 300);
      } else {
        // Restaurar posición si no se superó el umbral
        console.log('TouchEnd - Restaurando posición');
        bottomSheetContainer.style.transform = '';
      }
    }
  }

  private findBottomSheetContainer(): HTMLElement | null {
    // Buscar el contenedor padre más cercano que sea el bottom sheet container
    return this.element.closest('.mat-bottom-sheet-container') as HTMLElement;
  }
}
